import ProductCard from "~/components/product/ProductCard";
import Breadcrumb from "~/components/ui/Breadcrumb";
import { toAnalytics } from "~/sdk/ga4/transform/toAnalytics";
import { toProductItem } from "~/sdk/ga4/transform/toProductItem";
import trimProductForShelf from "~/sdk/trimProductForShelf";
import type { ProductListingPage } from "@decocms/apps-commerce/types";

import { SendEventOnView } from "~/components/Analytics";
import { useId } from "react";
import { getVtexConfig, vtexHost } from "@decocms/apps-vtex/client";
import { legacyProductList } from "@decocms/apps-vtex/loaders/legacy";
import { getUser } from "@decocms/apps-vtex/loaders/user";
import { getWishlist } from "@decocms/apps-vtex/loaders/wishlist";
import { parseCookie } from "@decocms/apps-vtex/utils/vtexId";
export interface Props {
    page: ProductListingPage | null;
}
function WishlistGallery({ page, isMobile, isLogged }: Awaited<ReturnType<typeof loader>>) {
    const id = useId();
    const isEmpty = !page?.products.length;
    if (isEmpty) {
        return (<div className='container'>
                <div className='mx-10 my-20 flex flex-col gap-4 justify-center items-center'>
                    <span className='font-medium text-2xl'>
                        Sua <h1 className='inline'>lista de desejos</h1> está vazia =(
                    </span>
                    <span>
                        {!isLogged ? <a className="font-bold" href={`/login?returnUrl=/listadedesejos`}>Faça login</a> : null}{!isLogged ? ' e adicione' : 'Adicione'}{' '}
                        itens para sua lista de desejos. Eles aparecerão aqui.
                    </span>
                </div>
            </div>);
    }
    const viewItemListEvent = toAnalytics({
        type: 'view_item_list',
        data: {
            items: page.products.map((product, index) => toProductItem(product, { quantity: 1, index })),
            view: {
                id: 'wishlist',
                name: 'Lista de desejos',
            },
        },
    });
    return (<div id={id} className='max-w-[1810px] px-4 mx-auto mt-4 mb-12'>
            <div className='mb-4'>
                <Breadcrumb itemListElement={[
            {
                '@type': 'ListItem',
                item: '/listadedesejos',
                name: 'Listas de desejos',
                position: 0,
            },
        ]}/>
            </div>

            <div className='p-4 mb-4 bg-neutral-200'>
                <span className='text-[#585858]'>
                    Há{' '}
                    <span className='text-red-wine font-bold'>
                        {page?.products.length}{' '}
                        {page.products.length === 1 ? 'produto' : 'produtos'}
                    </span>{' '}
                    na sua lista de desejos
                </span>
            </div>

            <ul className='grid grid-cols-[repeat(auto-fill,minmax(152px,1fr))] justify-between gap-6 sm:grid-cols-[repeat(auto-fill,minmax(200px,1fr))] lg:grid-cols-[repeat(auto-fill,minmax(315px,1fr))] lg:gap-8'>
                {page?.products.map((product, index) => (<li>
                        <ProductCard product={product} isMobile={isMobile} itemListId='wishlist' itemListName='Lista de desejos' index={index}/>
                    </li>))}
            </ul>

            <SendEventOnView id={id} event={viewItemListEvent}/>
        </div>);
}
export const loader = async (props: Props & { isMobile?: boolean }, req?: Request) => {
    const cookie = req?.headers.get("cookie") ?? "";
    const user = await getUser(cookie);
    const isLogged = !!user?.email;
    const isMobile = props.isMobile ?? false;

    // The CMS block wires `page` to vtex/loaders/product/wishlist.ts, but the
    // page resolver calls commerce loaders without the Request, so the auth
    // cookie never reaches VTEX and `page` arrives null even when logged in.
    // This loader does get the Request — fetch the wishlist SKUs here.
    let skus = props.page?.products?.map(({ sku }) => String(sku)) ?? [];
    if (!skus.length && isLogged && req) {
        const { payload } = parseCookie(req.headers, getVtexConfig().account);
        const shopperId = payload?.sub;
        if (shopperId) {
            const items = await getWishlist(cookie, { shopperId, allRecords: true });
            skus = items.map(({ sku }) => String(sku));
        }
    }

    if (!skus.length) {
        return {
            ...props,
            page: null,
            products: [],
            isMobile,
            isLogged,
        };
    }
    const baseUrl = `https://${vtexHost()}`;
    const raw = (await legacyProductList({ query: { skuIds: skus }, baseUrl })) ?? [];
    // Trim to what ProductCard renders before this hits the payload — the
    // legacy loader ships full variants (~289 KB/product). See
    // ~/sdk/trimProductForShelf.
    const products = raw.map(trimProductForShelf);
    const page = { ...props.page, products } as ProductListingPage;
    return {
        ...props,
        page,
        isMobile,
        isLogged,
    };
};
export default WishlistGallery;
